import React from "react";

/**
 * DECISION 009 — "Posted by <agent>" on a request an AGENCY posted for this club.
 *
 * After a club one-click-confirms an agency's marketplace request, that request
 * appears in the club's OWN "My Requests" with a working pitch-management page.
 * Without this line it is indistinguishable from a request the club wrote
 * itself, and the club manages pitches on a request it never wrote.
 *
 * Fed by `tender.agent_attribution` from the backend
 * (TenderClubDetailSerializer), which is `null` for every request the club
 * posted itself — so the line renders exactly on the agency-posted rows and
 * nowhere else. Only the client club ever receives such a row; the queryset in
 * TenderViewSet decides that, never this component.
 *
 * Wording and markup are the platform's existing attribution idiom, taken from
 * the agent-request marketplace card (components/agent-tender/AgentTenderCard —
 * "Posted by {responsible_agent.display_name}"), so the same phrase carries the
 * same meaning on both sides and the existing "Posted by" translation applies.
 *
 * CONTENT CONTRACT: an attribution line is never truncated and never removed.
 * That is why there is no `truncate` here — a long agent or agency name wraps
 * (`break-words`) instead of being clipped, and the line renders on every
 * surface that shows the club this request (grid card, list row, pitches-page
 * header, request-details modal).
 *
 * SECOND CASE (2026-08-02), same line, same words: "who at MY club posted this
 * request". A club has several people with access, and on the pitches page the
 * one who wrote the request is the person to ask about it. That is a DIFFERENT
 * fact from the agency case above — `agent_attribution` answers "an agency
 * posted this for us", `Tender.created_by` answers "a colleague posted this" —
 * so it arrives through the neutral `name` / `org` props instead of being
 * squeezed into the attribution shape. The agency attribution WINS when both
 * exist: it is the more surprising fact, and printing both would read as two
 * authors.
 */
const PostedByAgentLine = ({ attribution, name, org, className = "" }) => {
  const fromAgency = !!attribution?.agent_name;
  const primaryName = attribution?.agent_name || name || null;
  if (!primaryName) return null;
  const secondaryName = fromAgency ? attribution?.agency_name : org;

  return (
    <div
      className={`text-[11.5px] leading-snug text-[#64748B] break-words ${className}`}
    >
      {/* Explicit space node so the runtime translator, which trims the
          "Posted by" text node, can't glue it to the name. */}
      Posted by{" "}
      <span className="font-semibold text-[#374151]">{primaryName}</span>
      {secondaryName ? (
        <span className="text-[#9CA3AF]">{` · ${secondaryName}`}</span>
      ) : null}
    </div>
  );
};

export default PostedByAgentLine;
